import type { View } from '@perspective-dev/client'
import type { HTMLPerspectiveViewerElement } from '@perspective-dev/viewer'
import type { HTMLPerspectiveViewerDatagridPluginElement } from '@perspective-dev/viewer-datagrid'

export type DatagridColumnsConfig = NonNullable<
  Parameters<HTMLPerspectiveViewerDatagridPluginElement['restore']>[1]
>

/**
 * Keep expansion in the engine view: V5.5.1 auto-pause deletes it when hidden.
 * Reports deliberately keep processing updates in background/offscreen views.
 */
export const preserveReportView = (
  viewer: Pick<HTMLPerspectiveViewerElement, 'setAutoPause'>
): Promise<void> => viewer.setAutoPause(false)

/** Retain report-only datetime settings while accepting native styles and widths. */
export const mergeReportDatagridConfig = (
  configured: DatagridColumnsConfig,
  current: DatagridColumnsConfig
): DatagridColumnsConfig =>
  Object.fromEntries(
    Object.entries({ ...configured, ...current }).map(([field, column]) => {
      const dateFormat = configured[field]?.date_format

      return [
        field,
        dateFormat
          ? {
              ...column,
              date_format: {
                ...dateFormat,
                ...column.date_format,
                locale: dateFormat.locale,
                hour12: dateFormat.hour12
              }
            }
          : column
      ]
    })
  )

/**
 * V5.5.1 strips columns_config when a hidden group column has no view-schema
 * entry. Reapply formatting through the plugin so its row headers keep the
 * company locale and naive datetime timezone. The grouped-datetime browser
 * regression must pass without this call before removing the workaround.
 */
export const restoreReportDatagridConfig = async (
  datagrid: HTMLPerspectiveViewerDatagridPluginElement | null,
  columnsConfig: DatagridColumnsConfig,
  view: View
): Promise<void> => {
  if (datagrid) {
    datagrid.restore(datagrid.save(), columnsConfig)
    // V5 restore transfers state without drawing; update applies the formatters.
    await datagrid.update(view)
  }
}
